// components/GeneratedThumb/PlayGlyph.tsx — the disabled play control shown over a
// thumbnail. Shared by VideoDetailPage (over a neutral surface) and
// ShortsPlayerPage (over a full-bleed thumb) — was two copy-pasted CSS-triangle
// implementations; now one component using the shared `play` icon (SVG, never
// emoji — see mobile's Icon for the same rule).
//
// Contract: always used inside <DisabledHint>, which supplies `describedBy`. Per
// the DisabledHint contract (see ui/DisabledHint), this uses aria-disabled + stays
// focusable + suppresses its click — never the native `disabled` attribute.
import styled from 'styled-components';
import { Icon } from '@/icons';

const Glyph = styled.div<{ $size: number; $tone: 'surface' | 'overlay' }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: not-allowed;

  ${({ $tone, theme }) =>
    $tone === 'surface'
      ? `
        background: ${theme.color.surfaceRaised};
        border: 1px solid ${theme.color.glassBorder};
        color: ${theme.color.textMuted};
      `
      : `
        background: rgba(0, 0, 0, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.5);
        color: rgba(255, 255, 255, 0.85);
      `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

export interface PlayGlyphProps {
  /** From DisabledHint's render prop. */
  describedBy: string;
  size?: number;
  tone?: 'surface' | 'overlay';
}

export function PlayGlyph({ describedBy, size = 64, tone = 'surface' }: PlayGlyphProps) {
  return (
    <Glyph
      $size={size}
      $tone={tone}
      role="button"
      aria-disabled="true"
      aria-describedby={describedBy}
      aria-label="Play (unavailable)"
      tabIndex={0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <Icon name="play" size={Math.round(size * 0.4)} />
    </Glyph>
  );
}
